import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  RefreshCw, Sparkles, Copy, Trash2, Flame, TrendingUp, Radar,
  Globe, MessageSquare, Newspaper, Instagram, Twitter, Clock, Zap, AlertTriangle,
  History, CheckCircle2, XCircle, ExternalLink, Users, Calculator, Images, FileText
} from "lucide-react";
import Portale from "@/Portale";
import Leads from "@/Leads";
import Presentazione from "@/Presentazione";
import PianoAzione from "@/PianoAzione";
import Materiale from "@/Materiale";
import InstagramAnalytics from "@/Instagram";
import PianoContenuti from "@/PianoContenuti";
import OroLanding from "@/OroLanding";
import Privacy from "@/Privacy";
import NoteLegali from "@/NoteLegali";
import GoogleAds from "@/GoogleAds";
import Metodo from "@/Metodo";
import ErrorBoundary from "@/ErrorBoundary";

const GUIDA_ORO_PDF = "https://customer-assets-rejwkqb3.emergentagent.net/job_viral-wealth-watch/artifacts/2wgwnaee_Guida_Oro_Fisico_Alex_Gold%281%29.pdf";
function GuidaOro() {
  const [f, setF] = useState({ nome: "", email: "", privacy: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const provenienza = (() => {
    const p = new URLSearchParams(window.location.search);
    const src = (p.get("utm_source") || "").trim();
    const med = (p.get("utm_medium") || "").trim();
    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    if (src) return med ? `${cap(src)} · ${med}` : cap(src);
    return "Guida";
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome.trim() || !f.email.trim()) { toast.error("Inserisci nome ed email."); return; }
    if (!f.privacy) { toast.error("Devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        tipo: "cliente", nome: f.nome.trim(), email: f.email.trim(), telefono: "",
        provenienza, messaggio: "[Landing /oro] Ha richiesto la Guida gratuita all'oro fisico.",
      });
      setDone(true);
      setTimeout(() => window.open(GUIDA_ORO_PDF, "_blank"), 400);
    } catch { toast.error("Invio non riuscito. Riprova."); }
    finally { setLoading(false); }
  };

  const ci = "w-full rounded-xl px-4 py-3 text-[15px] outline-none border";
  const cis = { background: "#211f1c", borderColor: "#3a3733", color: "#EDE7DB" };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#EDE7DB", fontFamily: "Georgia, 'Times New Roman', serif", padding: 24 }} data-testid="guida-page">
      <Toaster theme="dark" position="top-center" />
      <div className="w-full max-w-md rounded-2xl p-7 shadow-2xl border" style={{ background: "#161513", color: "#EDE7DB", borderColor: "#3a3733" }}>
        {done ? (
          <div className="text-center py-4" data-testid="guida-success">
            <p className="text-5xl mb-3">📘</p>
            <h1 className="font-bold text-2xl mb-2">La tua Guida è pronta!</h1>
            <p className="text-sm mb-5" style={{ color: "#c9c3b6" }}>Si è aperta in una nuova scheda. Ti ho inviato anche una copia via email 📩</p>
            <a href={GUIDA_ORO_PDF} target="_blank" rel="noopener noreferrer" data-testid="guida-download-btn"
              className="block w-full rounded-full py-3.5 font-semibold text-[15px]" style={{ background: "#C9A227", color: "#161513" }}>
              Scarica di nuovo la Guida
            </a>
            <Link to="/oro" className="block mt-3 text-sm underline" style={{ color: "#c9c3b6" }} data-testid="guida-to-oro">
              Vuoi parlarne? Prenota una call riservata →
            </Link>
          </div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.25em] mb-3" style={{ color: "#C9A227" }}>Guida gratuita</p>
            <h1 className="font-bold text-2xl leading-tight mb-2">Oro fisico, spiegato senza fumo</h1>
            <p className="text-sm mb-5" style={{ color: "#c9c3b6" }}>Costi, premio, spread e come proteggere la liquidità ferma. Lascia nome ed email: te la mando subito.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="guida-form">
              <input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} placeholder="Nome" data-testid="guida-input-nome" className={ci} style={cis} />
              <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="Email" data-testid="guida-input-email" className={ci} style={cis} />
              <label className="flex items-start gap-2.5 text-[12px] leading-snug cursor-pointer" style={{ color: "#c9c3b6" }}>
                <input type="checkbox" checked={f.privacy} onChange={(e) => setF({ ...f, privacy: e.target.checked })} data-testid="guida-input-privacy" className="mt-0.5 w-4 h-4 shrink-0" />
                <span>Ho letto e accetto l'<Link to="/privacy" className="underline" style={{ color: "#EDE7DB" }}>informativa privacy</Link>.</span>
              </label>
              <button type="submit" disabled={loading} data-testid="guida-submit-btn"
                className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-[15px] disabled:opacity-60" style={{ background: "#6E1F2A", color: "#EDE7DB" }}>
                {loading ? "Invio…" : "📘 Ricevi la Guida gratuita"}
              </button>
              <p className="text-center text-[11px]" style={{ color: "#8a857a" }}>Niente spam. Solo la guida e qualche consiglio utile.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcon = (p) => {
  const k = (p || "").toLowerCase();
  if (k.includes("reddit")) return <MessageSquare size={13} />;
  if (k.includes("x") || k.includes("twitter")) return <Twitter size={13} />;
  if (k.includes("insta")) return <Instagram size={13} />;
  if (k.includes("news")) return <Newspaper size={13} />;
  return <Globe size={13} />;
};

const viralityClass = (s) =>
  s >= 75 ? "text-red-500" : s >= 50 ? "text-yellow-400" : "text-zinc-500";

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono tracking-[0.2em] uppercase border border-zinc-700 bg-zinc-800/50 text-zinc-300 ${className}`}>
    {children}
  </span>
);

function TrendCard({ trend, onGenerate, generating }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-zinc-900 border border-zinc-800 p-5 transition-colors hover:border-zinc-600 relative"
      data-testid="trend-card"
    >
      <div className="flex items-center justify-between mb-3">
        <Badge>{platformIcon(trend.platform)} {trend.source}</Badge>
        <div className={`flex items-center gap-1.5 font-mono text-sm font-medium ${viralityClass(trend.virality_score)}`}>
          <Flame size={15} /> {trend.virality_score}
        </div>
      </div>
      <h3 className="font-head font-semibold text-lg leading-snug text-zinc-50 mb-2" data-testid="trend-title">
        {trend.title}
      </h3>
      <p className="text-sm text-zinc-400 leading-relaxed mb-3">{trend.summary}</p>
      <div className="border-l-2 border-red-600 pl-3 mb-4">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Perché è in trend</p>
        <p className="text-sm text-zinc-300">{trend.why_trending}</p>
      </div>
      <button
        onClick={() => onGenerate(trend)}
        disabled={generating}
        data-testid="generate-script-button"
        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 py-2.5 text-sm font-medium transition-colors"
      >
        {generating ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
        {generating ? "Genero..." : "Genera Hook & Script"}
      </button>
    </motion.div>
  );
}

function GeneratorPanel({ script, loading }) {
  const [tab, setTab] = useState("hooks");
  useEffect(() => { if (script) setTab("hooks"); }, [script]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    toast.success(`${label} copiato negli appunti`);
  };

  const fullScript = script
    ? [
        `TITOLO: ${script.titolo}`,
        `DURATA: ${script.durata}`,
        "",
        ...script.sezioni.map((s) => `[${s.tempo}] ${s.testo}`),
        "",
        `CTA: ${script.cta}`,
        "",
        `CAPTION: ${script.caption}`,
        `${(script.hashtags || []).join(" ")}`,
      ].join("\n")
    : "";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-center" data-testid="generator-loading">
        <Zap size={34} className="text-red-500 rp-pulse mb-4" />
        <p className="font-head font-semibold text-zinc-200">Sto scrivendo i tuoi hook virali...</p>
        <p className="text-sm text-zinc-500 mt-1">Claude sta lavorando allo script</p>
      </div>
    );
  }

  if (!script) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6" data-testid="generator-empty">
        <div className="border border-zinc-800 p-4 mb-4"><Sparkles size={28} className="text-zinc-600" /></div>
        <p className="font-head font-semibold text-zinc-300">Nessuno script generato</p>
        <p className="text-sm text-zinc-500 mt-1 max-w-xs">
          Seleziona un trend e premi "Genera Hook & Script" per ottenere 5 hook e lo script completo del video.
        </p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} data-testid="generator-result">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head font-bold text-lg text-zinc-50 truncate pr-2">{script.titolo}</h3>
        <Badge><Clock size={11} /> {script.durata}</Badge>
      </div>

      <div className="flex border border-zinc-800 mb-4">
        {[["hooks", "5 Hook"], ["script", "Script"], ["post", "Post"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`flex-1 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
              tab === k ? "bg-red-600 text-white" : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "hooks" && (
        <div className="space-y-2" data-testid="hooks-list">
          {script.hooks.map((h, i) => (
            <div key={i} className="group flex items-start gap-3 bg-zinc-950 border border-zinc-800 p-3 hover:border-zinc-600 transition-colors">
              <span className="font-mono text-red-500 text-sm mt-0.5">0{i + 1}</span>
              <p className="text-sm text-zinc-200 flex-1">{h}</p>
              <button onClick={() => copy(h, "Hook")} data-testid={`copy-hook-${i}`} className="text-zinc-600 hover:text-white transition-colors">
                <Copy size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "script" && (
        <div data-testid="script-body">
          <div className="space-y-3 mb-4">
            {script.sezioni.map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-red-500 pt-1 w-16 shrink-0">{s.tempo}</span>
                <p className="text-sm text-zinc-200 leading-relaxed">{s.testo}</p>
              </div>
            ))}
          </div>
          <div className="border-l-2 border-yellow-400 pl-3 mb-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Call to action</p>
            <p className="text-sm text-zinc-200">{script.cta}</p>
          </div>
          <button onClick={() => copy(fullScript, "Script completo")} data-testid="copy-script-button"
            className="w-full flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-700 text-zinc-200 hover:bg-zinc-800 py-2.5 text-sm transition-colors">
            <Copy size={15} /> Copia script completo
          </button>
        </div>
      )}

      {tab === "post" && (
        <div data-testid="post-body">
          <p className="text-sm text-zinc-200 leading-relaxed mb-3 bg-zinc-950 border border-zinc-800 p-3">{script.caption}</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {(script.hashtags || []).map((h, i) => (
              <span key={i} className="text-xs font-mono text-yellow-400">{h}</span>
            ))}
          </div>
          <button onClick={() => copy(`${script.caption}\n\n${(script.hashtags || []).join(" ")}`, "Caption")} data-testid="copy-caption-button"
            className="w-full flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-700 text-zinc-200 hover:bg-zinc-800 py-2.5 text-sm transition-colors">
            <Copy size={15} /> Copia caption + hashtag
          </button>
        </div>
      )}
    </motion.div>
  );
}

function Dashboard() {
  const [trends, setTrends] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [script, setScript] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [live, setLive] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("scripts");
  const [niches, setNiches] = useState([]);
  const [activeNiche, setActiveNiche] = useState("protezione_patrimoniale");

  const loadTrends = useCallback(async () => {
    const { data } = await axios.get(`${API}/trends`);
    setTrends(data);
  }, []);
  const loadScripts = useCallback(async () => {
    const { data } = await axios.get(`${API}/scripts`);
    setScripts(data);
  }, []);
  const loadLogs = useCallback(async () => {
    try {
      const [{ data: l }, { data: s }] = await Promise.all([
        axios.get(`${API}/logs`),
        axios.get(`${API}/logs/stats`),
      ]);
      setLogs(l);
      setLogStats(s);
    } catch { /* noop */ }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/config`);
      setLive(data.live_search);
      setLastError(data.last_error || null);
      setRetrying(data.retrying || null);
      if (data.active_niche) setActiveNiche(data.active_niche);
    } catch { /* noop */ }
  }, []);

  const loadNiches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/niches`);
      setNiches(data.niches || []);
      if (data.active) setActiveNiche(data.active);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadConfig();
    loadNiches();
    loadTrends();
    loadScripts();
    loadLogs();
  }, [loadConfig, loadNiches, loadTrends, loadScripts, loadLogs]);

  const refresh = async (nicheKey) => {
    const key = nicheKey || activeNiche;
    setLoadingTrends(true);
    try {
      const { data } = await axios.post(`${API}/trends/refresh`, null, { params: { niche: key } });
      setTrends(data);
      setLastError(null);
      setRetrying(null);
      loadLogs();
      toast.success(`${data.length} trend aggiornati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel recupero dei trend");
      loadConfig();
      loadLogs();
    } finally {
      setLoadingTrends(false);
    }
  };

  const changeNiche = (key) => {
    setActiveNiche(key);
    setTrends([]);
    setScript(null);
    refresh(key);
  };

  const generate = async (trend) => {
    setGeneratingId(trend.id);
    setGenLoading(true);
    setScript(null);
    try {
      const { data } = await axios.post(`${API}/generate`, {
        title: trend.title, summary: trend.summary, angle: trend.angle,
        niche: trend.niche || activeNiche,
      });
      setScript(data);
      setScripts((prev) => [data, ...prev]);
      toast.success("Script generato!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setGeneratingId(null);
      setGenLoading(false);
    }
  };

  const removeScript = async (id, e) => {
    e.stopPropagation();
    await axios.delete(`${API}/scripts/${id}`);
    setScripts((p) => p.filter((s) => s.id !== id));
    if (script?.id === id) setScript(null);
    toast.success("Script eliminato");
  };

  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="App bg-zinc-950 text-zinc-50 min-h-screen">
      <Toaster theme="dark" position="top-right" />

      {/* Header */}
      <header className="border-b border-zinc-800 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/95 backdrop-blur z-20">
        <div className="flex items-center gap-3">
          <div className="bg-red-600 p-2"><Radar size={20} className="text-white" /></div>
          <div>
            <h1 className="font-head font-extrabold text-xl tracking-tight leading-none">RADAR PATRIMONIALE</h1>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Viral Trend Radar · Video Brevi</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/portale" data-testid="link-portale"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <ExternalLink size={12} /> Portale
          </Link>
          <Link to="/calcolatore" data-testid="link-piano-azione"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <Calculator size={12} /> Calcolatore
          </Link>
          <Link to="/materiale" data-testid="link-materiale"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <Images size={12} /> Materiale
          </Link>
          <Link to="/instagram" data-testid="link-instagram"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <Instagram size={12} /> Analytics
          </Link>
          <Link to="/piano-contenuti" data-testid="link-piano-contenuti"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <FileText size={12} /> Contenuti
          </Link>
          <Link to="/lead" data-testid="link-lead"
            className="hidden sm:inline-flex items-center gap-1.5 border border-yellow-700/50 text-yellow-500 text-xs font-mono uppercase tracking-wider px-3 py-2 hover:bg-yellow-500/10 transition-colors">
            <Users size={12} /> Lead
          </Link>
          <select
            value={activeNiche}
            onChange={(e) => changeNiche(e.target.value)}
            disabled={loadingTrends}
            data-testid="niche-selector"
            className="bg-zinc-950 border border-zinc-700 rounded-none text-zinc-100 text-sm px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50 cursor-pointer font-sans"
          >
            {niches.map((n) => (
              <option key={n.key} value={n.key} className="bg-zinc-900">{n.label}</option>
            ))}
          </select>
          <Badge className="text-zinc-400 border-zinc-700 hidden lg:inline-flex" data-testid="auto-schedule-badge">
            <Clock size={11} /> Auto 07:00 CET
          </Badge>
          <Badge className={live ? "text-emerald-400 border-emerald-700" : "text-yellow-400 border-yellow-700"}>
            {live ? "Ricerca Live" : "Modalità AI"}
          </Badge>
          <img src="/alex-gold.jpg"
            alt="Alex Gold - Protezione Patrimoniale" className="w-9 h-9 object-cover object-top border border-zinc-700 hidden sm:block" />
        </div>
      </header>

      {/* Grid */}
      <main className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 px-4 md:px-8 py-6 max-w-[1600px] mx-auto">
        {/* Sidebar */}
        <aside className="md:col-span-3 order-3 md:order-1">
          <div className="flex border border-zinc-800 mb-4">
            <button onClick={() => setSidebarTab("scripts")} data-testid="sidebar-tab-scripts"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-mono uppercase tracking-[0.15em] transition-colors ${
                sidebarTab === "scripts" ? "bg-red-600 text-white" : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"}`}>
              <Sparkles size={12} /> Script ({scripts.length})
            </button>
            <button onClick={() => { setSidebarTab("logs"); loadLogs(); }} data-testid="sidebar-tab-logs"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-mono uppercase tracking-[0.15em] transition-colors ${
                sidebarTab === "logs" ? "bg-red-600 text-white" : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"}`}>
              <History size={12} /> Log
            </button>
          </div>

          {sidebarTab === "scripts" && (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto scroll-thin pr-1" data-testid="scripts-history">
              {scripts.length === 0 && <p className="text-sm text-zinc-600">Nessuno script salvato.</p>}
              {scripts.map((s) => (
                <div key={s.id} onClick={() => setScript(s)} data-testid="history-item"
                  className="group cursor-pointer bg-zinc-900 border border-zinc-800 hover:border-zinc-600 p-3 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-zinc-200 leading-snug line-clamp-2">{s.titolo}</p>
                    <button onClick={(e) => removeScript(s.id, e)} data-testid="delete-script-button"
                      className="text-zinc-700 hover:text-red-500 transition-colors shrink-0"><Trash2 size={13} /></button>
                  </div>
                  <p className="text-[10px] font-mono text-zinc-600 mt-1">{new Date(s.created_at).toLocaleString("it-IT")}</p>
                </div>
              ))}
            </div>
          )}

          {sidebarTab === "logs" && (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto scroll-thin pr-1" data-testid="logs-history">
              {logStats && logStats.total > 0 && (
                <div className="bg-zinc-900 border border-zinc-800 p-4 mb-2" data-testid="reliability-indicator">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Affidabilità · 7 giorni</p>
                    <span className={`font-head font-extrabold text-2xl ${
                      logStats.rate >= 90 ? "text-emerald-400" : logStats.rate >= 60 ? "text-yellow-400" : "text-red-500"}`}>
                      {logStats.rate}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 overflow-hidden">
                    <div className={`h-full transition-all ${
                      logStats.rate >= 90 ? "bg-emerald-500" : logStats.rate >= 60 ? "bg-yellow-400" : "bg-red-500"}`}
                      style={{ width: `${logStats.rate}%` }} />
                  </div>
                  <p className="text-[11px] font-mono text-zinc-500 mt-2">
                    {logStats.success} successi · {logStats.failed} fallimenti · {logStats.total} totali
                  </p>
                </div>
              )}
              {logs.length === 0 && <p className="text-sm text-zinc-600">Nessun aggiornamento registrato.</p>}
              {logs.map((l) => {
                const ok = l.status === "success";
                const srcLabel = l.source === "auto" ? "Auto 07:00" : l.source === "retry" ? `Retry #${l.attempt}` : "Manuale";
                return (
                  <div key={l.id} data-testid="log-item"
                    className={`bg-zinc-900 border-l-2 ${ok ? "border-emerald-500" : "border-red-500"} border-y border-r border-zinc-800 p-3`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-sm font-medium">
                        {ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-500" />}
                        <span className={ok ? "text-emerald-300" : "text-red-300"}>{ok ? "Successo" : "Fallito"}</span>
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{srcLabel}</span>
                    </div>
                    <p className="text-[11px] font-mono text-zinc-500 mt-1">{new Date(l.at).toLocaleString("it-IT")}</p>
                    {ok
                      ? <p className="text-xs text-zinc-400 mt-1">{l.count} trend recuperati</p>
                      : l.message ? <p className="text-xs text-red-300/70 mt-1 line-clamp-2">{l.message}</p> : null}
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* Feed */}
        <section className="md:col-span-5 order-1 md:order-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-head font-bold text-2xl tracking-tight flex items-center gap-2">
                <TrendingUp size={22} className="text-red-500" /> Trend del Giorno
              </h2>
              <p className="text-sm text-zinc-500 capitalize">
                {(niches.find((n) => n.key === activeNiche)?.label) || ""} · {today}
              </p>
            </div>
            <button onClick={refresh} disabled={loadingTrends} data-testid="refresh-trends-button"
              className="flex items-center gap-2 bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 px-4 py-2.5 text-sm font-medium transition-colors">
              <RefreshCw size={15} className={loadingTrends ? "animate-spin" : ""} />
              {loadingTrends ? "Cerco..." : "Aggiorna"}
            </button>
          </div>

          {retrying && !lastError && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="trend-retry-banner"
              className="border border-yellow-800 bg-yellow-950/30 p-4 mb-4 flex items-start gap-3"
            >
              <RefreshCw size={18} className="text-yellow-400 shrink-0 mt-0.5 animate-spin" />
              <div className="flex-1">
                <p className="font-head font-semibold text-sm text-yellow-300">
                  Aggiornamento fallito — nuovo tentativo {retrying.attempt}/{retrying.max} in corso
                </p>
                <p className="text-sm text-yellow-200/80 mt-0.5">{retrying.message}</p>
                <p className="text-[11px] font-mono text-yellow-400/70 mt-1">
                  Prossimo tentativo: {new Date(retrying.next_at).toLocaleTimeString("it-IT")}
                </p>
              </div>
            </motion.div>
          )}

          {lastError && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="trend-error-banner"
              className="border border-red-800 bg-red-950/40 p-4 mb-4 flex items-start gap-3"
            >
              <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-head font-semibold text-sm text-red-300">
                  {lastError.source === "auto"
                    ? "Aggiornamento automatico delle 07:00 fallito"
                    : "Ultimo aggiornamento fallito"}
                </p>
                <p className="text-sm text-red-200/80 mt-0.5">{lastError.message}</p>
                <p className="text-[11px] font-mono text-red-400/70 mt-1">
                  {new Date(lastError.at).toLocaleString("it-IT")}
                  {trends.length > 0 ? " · Mostro gli ultimi trend disponibili (potrebbero essere obsoleti)." : ""}
                </p>
              </div>
              <button onClick={refresh} disabled={loadingTrends} data-testid="retry-refresh-button"
                className="flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 px-3 py-2 text-xs font-medium transition-colors shrink-0">
                <RefreshCw size={13} className={loadingTrends ? "animate-spin" : ""} /> Riprova
              </button>
            </motion.div>
          )}

          {loadingTrends && trends.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="trends-loading">
              <Radar size={36} className="text-red-500 rp-pulse mb-4" />
              <p className="font-head font-semibold text-zinc-200">Scansione di web, social e news...</p>
            </div>
          )}

          {!loadingTrends && trends.length === 0 && (
            <div className="border border-zinc-800 bg-zinc-900 p-10 text-center" data-testid="trends-empty">
              <Radar size={30} className="text-zinc-600 mx-auto mb-3" />
              <p className="font-head font-semibold text-zinc-300">Nessun trend ancora</p>
              <p className="text-sm text-zinc-500 mt-1">Premi "Aggiorna" per trovare i trend virali di oggi.</p>
            </div>
          )}

          <div className="space-y-4">
            <AnimatePresence>
              {trends.map((t) => (
                <TrendCard key={t.id} trend={t} onGenerate={generate} generating={generatingId === t.id} />
              ))}
            </AnimatePresence>
          </div>
        </section>

        {/* Generator */}
        <section className="md:col-span-4 order-2 md:order-3">
          <h2 className="font-head font-bold text-2xl tracking-tight flex items-center gap-2 mb-4">
            <Sparkles size={22} className="text-yellow-400" /> Generatore
          </h2>
          <div className="bg-zinc-900 border border-zinc-800 p-5 min-h-[400px] sticky top-24">
            <GeneratorPanel script={script} loading={genLoading} />
          </div>
        </section>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Portale />} />
        <Route path="/radar" element={<Dashboard />} />
        <Route path="/portale" element={<Portale />} />
        <Route path="/lead" element={<Leads />} />
        <Route path="/presentazione" element={<Presentazione />} />
        <Route path="/piano-azione" element={<Navigate to="/calcolatore" replace />} />
        <Route path="/calcolatore" element={<PianoAzione />} />
        <Route path="/materiale" element={<Materiale />} />
        <Route path="/instagram" element={<InstagramAnalytics />} />
        <Route path="/piano-contenuti" element={<PianoContenuti />} />
        <Route path="/oro" element={<OroLanding />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/note-legali" element={<NoteLegali />} />
        <Route path="/google-ads" element={<GoogleAds />} />
        <Route path="/metodo" element={<Metodo />} />
        <Route path="/guida" element={<GuidaOro />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
